import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { PLATFORM_COLLECTIONS } from '../utils/platformSchema';

/**
 * Load student-facing review notes (written by Pass 36 apply actions).
 * Filters for clientNotes with visibility='student' and tag 'ai_student_note'.
 *
 * @param {string|null} studentId
 * @param {string|null} orgId
 * @param {number} [maxResults=10]
 * @returns {{ reviews: object[], loading: boolean }}
 */
export function useStudentReviews(studentId, orgId, maxResults = 10) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!studentId || !orgId) return;
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const ref = collection(db, PLATFORM_COLLECTIONS.CLIENT_NOTES);
        const q = query(
          ref,
          where('clientUserId', '==', studentId),
          where('organizationId', '==', orgId),
          where('visibility', '==', 'student')
        );
        const snap = await getDocs(q);

        if (cancelled) return;

        const notes = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((n) => Array.isArray(n.tags) && n.tags.includes('ai_student_note'))
          .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
          .slice(0, maxResults);

        setReviews(notes);
      } catch (_) {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [studentId, orgId, maxResults]);

  return { reviews, loading };
}

function toMs(ts) {
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  return new Date(ts).getTime() || 0;
}
